import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function ErrorBoundariesPage(): Node {
  return html`
    <div>
      <h1>Error Boundaries</h1>
      <p><code>ErrorBoundary</code> catches errors thrown during rendering or in async operations. Instead of crashing the whole app, it shows a fallback UI.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { ErrorBoundary, html } from 'onefold';

function App(): Node {
  return html\`
    <div>
      \${ErrorBoundary(
        () => RiskyComponent(),
        (error) => html\`
          <div class="error-card">
            <h3>Something went wrong</h3>
            <p>\${error.message}</p>
            <button onclick=\${() => location.reload()}>Retry</button>
          </div>
        \`
      )}
    </div>
  \`;
}`)}

      <h2>With Suspense</h2>
      <p>Combine ErrorBoundary with Suspense for complete async handling:</p>
      ${CodeBlock(`import { ErrorBoundary, html } from 'onefold';
import { Suspense } from 'onefold/suspense';

function App(): Node {
  return html\`
    <div>
      \${ErrorBoundary(
        () => Suspense(
          () => AsyncDataComponent(),
          () => html\`<p>Loading...</p>\`
        ),
        (error) => html\`<p class="error">Failed: \${error.message}</p>\`
      )}
    </div>
  \`;
}`)}

      ${Callout('ErrorBoundary catches both synchronous render errors and rejected promises from async components.')}

      <h2>Nested Boundaries</h2>
      ${CodeBlock(`function App(): Node {
  return html\`
    <div>
      \${ErrorBoundary(
        () => html\`
          <div>
            <header>\${Header()}</header>
            \${ErrorBoundary(
              () => MainContent(),
              (err) => html\`<p>Content failed: \${err.message}</p>\`
            )}
            \${ErrorBoundary(
              () => Sidebar(),
              (err) => html\`<p>Sidebar unavailable</p>\`
            )}
          </div>
        \`,
        (err) => html\`<p>App crashed: \${err.message}</p>\`
      )}
    </div>
  \`;
}`)}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>ErrorBoundary</code></td><td>(content: () => Node, fallback: (error: Error) => Node)</td><td>Catch errors in content and render fallback.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Click "Trigger Error" to see the ErrorBoundary catch it, then "Retry" to recover:</p>

      ${Playground(`import { html, mount, createSignal, ErrorBoundary } from 'onefold';

function App() {
  const attempt = createSignal(0);

  // Each render attempt might throw
  function RiskyComponent() {
    // Randomly fail 50% of the time
    if (Math.random() > 0.5) {
      throw new Error('Random failure! (50% chance on each render)');
    }
    return html\`
      <div style="padding:16px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px">
        <h4 style="color:#166534">Success! (attempt #\${attempt()})</h4>
        <p style="font-size:13px">The component rendered without errors this time.</p>
      </div>
    \`;
  }

  // ErrorBoundary wraps a render function in try/catch
  // If it throws, fallback is shown with the error and a retry function
  function renderBoundary() {
    return ErrorBoundary(
      () => RiskyComponent(),
      (error, retry) => html\`
        <div style="padding:16px;background:#fef2f2;border:1px solid #fca5a5;border-radius:8px">
          <h4 style="color:#991b1b;margin-bottom:8px">Error Caught!</h4>
          <p style="font-size:13px;color:#666;margin-bottom:12px">\${error.message}</p>
          <button onclick=\${retry}>Retry (re-render)</button>
        </div>
      \`
    );
  }

  return html\`
    <div>
      <h3>ErrorBoundary Demo</h3>
      <p style="font-size:13px;color:#666;margin-bottom:12px">
        RiskyComponent throws 50% of the time. Click "Re-mount" to create a new boundary and try again.
      </p>
      <button onclick=\${() => attempt.set(n => n + 1)} style="margin-bottom:12px">Re-mount Component</button>
      \${() => { attempt(); return renderBoundary(); }}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'ErrorBoundary — Catch & Retry')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/async/suspense">Suspense</a> — show fallback UI while async content loads</li>
        <li><a href="/observability">Observability</a> — monitor and debug your application</li>
      </ul>
    </div>
  `;
}
